import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getElectronAPI, isElectron } from '../../utils/electron';
import { apiClient } from '../../api/client';
import { useAuth } from '../../context/AuthContext';

const TOKEN_PREFIX = 'nyx_pa_';

export default function PersonaConfig() {
  const { t } = useTranslation();
  const { token: authToken } = useAuth();
  const [token, setToken] = useState('');
  const [hasToken, setHasToken] = useState(false);
  const [circles, setCircles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [hint, setHint] = useState(null);

  const probe = useCallback(async (force = false) => {
    setLoading(true);
    setError(null);
    setHint(null);
    try {
      const { circles: list, hint: apiHint } = await apiClient.listPersonas({ force });
      setCircles(Array.isArray(list) ? list : []);
      setHasToken(true);
      if (apiHint) setHint(apiHint);
    } catch (err) {
      setHasToken(false);
      setCircles([]);
      if (!/no persona studio token/i.test(err.message || '')) {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    probe();
  }, [probe]);

  const handleSave = async () => {
    const trimmed = token.trim();
    if (!trimmed.startsWith(TOKEN_PREFIX)) {
      setError(t('settings.personaTokenInvalid', 'Token muss mit nyx_pa_ beginnen'));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      if (isElectron) {
        const api = getElectronAPI();
        if (!api?.savePersonaToken) {
          throw new Error('Electron safeStorage nicht verfügbar');
        }
        const ok = await api.savePersonaToken(trimmed);
        if (!ok) throw new Error('Token konnte nicht gespeichert werden');
      } else {
        // Web mode: store as per-user override in user_settings (encrypted server-side).
        const res = await fetch('/api/secrets/me/persona_token', {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
          },
          body: JSON.stringify({ value: trimmed }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || 'Token konnte nicht gespeichert werden');
        }
      }

      // force=1 to bypass server-side cache (which may have stored an empty list from a previous call)
      const { circles: list, hint: apiHint } = await apiClient.listPersonas({ force: true });
      setCircles(Array.isArray(list) ? list : []);
      setHasToken(true);
      setToken('');
      if (apiHint) setHint(apiHint);
    } catch (err) {
      setError(err.message);
      setHasToken(false);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    setError(null);
    try {
      if (isElectron) {
        const api = getElectronAPI();
        if (api?.deletePersonaToken) await api.deletePersonaToken();
      } else {
        await fetch('/api/secrets/me/persona_token', {
          method: 'DELETE',
          headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
        });
      }
      setHasToken(false);
      setCircles([]);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const totalPersonas = circles.reduce((sum, c) => sum + (c.personas?.length || 0), 0);

  return (
    <div className="persona-config" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}>
        <span
          style={{
            width: 8, height: 8, borderRadius: '50%',
            background: hasToken ? 'var(--success)' : 'var(--text-tertiary)',
          }}
        />
        <span style={{ color: hasToken ? 'var(--success)' : 'var(--text-secondary)' }}>
          {hasToken
            ? t('settings.personaConnected', `Verbunden — ${circles.length} Kreis${circles.length === 1 ? '' : 'e'}, ${totalPersonas} Persona${totalPersonas === 1 ? '' : 's'}`)
            : t('settings.personaNotConnected', 'Nicht verbunden')}
        </span>
      </div>

      {error && (
        <div className="api-key-error">
          {error}
        </div>
      )}

      {!error && hint && (
        <div style={{
          padding: '0.6rem 0.75rem',
          background: 'var(--accent-dim)',
          color: 'var(--accent)',
          border: '1px solid var(--accent)',
          borderRadius: 'var(--radius)',
          fontSize: '0.8125rem',
        }}>
          {t('settings.personaHint', 'Hinweis')}: {hint}
        </div>
      )}

      {hasToken && (
        <button
          className="api-key-save-btn"
          onClick={() => probe(true)}
          disabled={loading}
          style={{ alignSelf: 'flex-start' }}
        >
          {loading ? t('settings.refreshing', 'Lade…') : t('settings.refresh', 'Aktualisieren')}
        </button>
      )}

      {!hasToken && (
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <input
            type="password"
            className="api-key-input"
            placeholder="nyx_pa_…"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSave()}
            style={{ flex: 1 }}
            autoComplete="off"
          />
          <button
            className="api-key-save-btn"
            onClick={handleSave}
            disabled={loading || !token.startsWith(TOKEN_PREFIX)}
          >
            {loading ? t('settings.personaConnecting', 'Prüfe…') : t('settings.connect', 'Verbinden')}
          </button>
        </div>
      )}

      {hasToken && (
        <button
          className="api-key-delete-btn"
          onClick={handleDelete}
          style={{ alignSelf: 'flex-start' }}
        >
          {t('settings.personaDisconnect', 'Token entfernen')}
        </button>
      )}

      {hasToken && circles.length > 0 && (
        <div>
          <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
            {t('settings.personaCircles', 'Persona-Kreise')} ({circles.length})
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {circles.map((c) => (
              <div
                key={c.id}
                style={{
                  padding: '0.6rem 0.75rem',
                  background: 'var(--surface)',
                  border: '1px solid var(--border-color)',
                  borderRadius: 'var(--radius)',
                  fontSize: '0.8125rem',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
                  <strong style={{ color: 'var(--text-primary)' }}>{c.name}</strong>
                  <span style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)' }}>
                    {c.personas?.length || 0} Persona{(c.personas?.length || 0) === 1 ? '' : 's'} · {c.currency || 'EUR'}
                  </span>
                </div>
                {c.tagline && (
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.15rem' }}>
                    {c.tagline}
                  </div>
                )}
                {(c.personas || []).length > 0 && (
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)', marginTop: '0.4rem' }}>
                    {c.personas.map(p => p.name).join(' · ')}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
